/**
 * PasteConversationDialog — Modal per enganxar converses WhatsApp en brut.
 *
 * Reutilitzable des de:
 *   - ClientDetailSheet / ClientDetailPage (amb clientId)
 *   - ReservationSheet (amb reservationId)
 *
 * El backend (POST /messaging/logs) parseja el text, extreu telèfon,
 * genera resum i desa el CommunicationLog.
 */

import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Loader2, ClipboardPaste } from 'lucide-react';
import { toast } from 'sonner';
import { useCreateCommunicationLog } from '@/hooks/useCommunicationLogs';

export interface PasteConversationDialogProps {
  /** Client ID to associate the log with */
  clientId?: string;
  /** Reservation ID to associate the log with */
  reservationId?: string;
  /** Dialog open state */
  open: boolean;
  /** Callback when dialog open state changes */
  onOpenChange: (open: boolean) => void;
  /** Callback after successful save */
  onSuccess?: () => void;
}

const PLACEHOLDER_TEXT = `Enganxa aquí la conversa de WhatsApp exportada.

Format esperat:
[21:47, 19/4/2026] +34 600 000 000: Hola, tinc una reserva...
[21:48, 19/4/2026] Hostal Guilleumes: Hola! Sí, la tenim confirmada.
[21:49, 19/4/2026] +34 600 000 000: Perfecte, gràcies!`;

export function PasteConversationDialog({
  clientId,
  reservationId,
  open,
  onOpenChange,
  onSuccess,
}: PasteConversationDialogProps) {
  const [rawContent, setRawContent] = useState('');
  const createLog = useCreateCommunicationLog();

  const handleSave = async () => {
    if (!rawContent.trim()) {
      toast.error('El contingut no pot estar buit');
      return;
    }

    try {
      await createLog.mutateAsync({
        rawContent: rawContent.trim(),
        clientId,
        reservationId,
        source: 'WHATSAPP_PASTE',
      });

      toast.success('Conversa guardada correctament');
      setRawContent('');
      onOpenChange(false);
      onSuccess?.();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Error desconegut';
      toast.error(`Error guardant conversa: ${message}`);
    }
  };

  const handleClose = (isOpen: boolean) => {
    if (!isOpen) {
      setRawContent('');
    }
    onOpenChange(isOpen);
  };

  const lineCount = rawContent.split('\n').filter((l) => l.trim()).length;

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-2xl max-h-[85vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ClipboardPaste className="h-5 w-5" />
            Enganxar conversa WhatsApp
          </DialogTitle>
          <DialogDescription>
            Enganxa el text exportat de WhatsApp. El sistema parsejarà els missatges automàticament
            i generarà un resum.
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 min-h-0 space-y-3">
          <Textarea
            value={rawContent}
            onChange={(e) => setRawContent(e.target.value)}
            placeholder={PLACEHOLDER_TEXT}
            className="min-h-[300px] max-h-[50vh] font-mono text-sm resize-y"
            autoFocus
          />
          {rawContent.trim() && (
            <p className="text-xs text-muted-foreground">
              {lineCount} {lineCount === 1 ? 'línia' : 'línies'} detectades
            </p>
          )}
        </div>

        <DialogFooter className="gap-2 sm:gap-0">
          <Button
            variant="outline"
            onClick={() => handleClose(false)}
            disabled={createLog.isPending}
          >
            Cancel·lar
          </Button>
          <Button onClick={handleSave} disabled={!rawContent.trim() || createLog.isPending}>
            {createLog.isPending ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Guardant...
              </>
            ) : (
              'Guardar conversa'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
